import { useEffect } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useUpdateAdministratorPhoneNumberMutation } from '../generated/urql.administrator'
import { PhoneNumberRegex } from '../lib/patterns'
import toastsService from '../lib/toastsService'

export type UpdateAdministratorPhoneNumberModalData = {
  isVisible: boolean
  id?: number
  phoneNumber?: string | null
}

type Props = {
  data: UpdateAdministratorPhoneNumberModalData
  onClose: () => void
  onSuccess: () => void
}

type FormValues = {
  phoneNumber: string
}

const UpdateAdministratorPhoneNumberModal = ({
  data: { isVisible, id, phoneNumber },
  onClose,
  onSuccess,
}: Props) => {
  const [, updateAdministratorPhoneNumber] = useUpdateAdministratorPhoneNumberMutation()
  const {
    handleSubmit,
    reset,
    register,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>()

  useEffect(() => {
    if (isVisible) {
      reset({ phoneNumber: phoneNumber ?? '' })
    }
  }, [isVisible, phoneNumber, reset])

  const onSubmit = async ({ phoneNumber }: FormValues) => {
    if (!id) return

    const { error } = await updateAdministratorPhoneNumber({ id, phoneNumber })

    if (error) {
      toastsService.add({ title: 'Błąd!', message: error.message, variant: 'danger' })
      return
    }

    toastsService.add({ title: 'Sukces!', message: 'Numer telefonu zaktualizowany', variant: 'success' })
    onSuccess()
    onClose()
  }

  return (
    <Modal show={isVisible} onHide={onClose} size="sm" centered>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Edycja nr telefonu</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="phoneNumberInput">
            <Form.Label>Nr telefonu</Form.Label>
            <Form.Control
              autoFocus
              {...register('phoneNumber', {
                required: 'Pole wymagane',
                pattern: { value: PhoneNumberRegex, message: 'Nieprawidłowy numer telefonu' },
              })}
              isInvalid={!!errors.phoneNumber}
            />
            <Form.Control.Feedback type="invalid">
              {errors.phoneNumber?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Anuluj
          </Button>
          <Button style={{ width: 75 }} type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? (
              <div className="flex justify-center w-24">
                <Spinner size="sm" animation="border" role="status" />
              </div>
            ) : (
              'Zapisz'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default UpdateAdministratorPhoneNumberModal
